import React, { useState, useEffect } from "react";
import "./Admin.css";

const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content: "",
    image: "",
    category: "Trading",
    author: "SPi Academi",
    date: new Date().toISOString().split('T')[0]
  });
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Load blogs from localStorage
  useEffect(() => {
    const savedBlogs = localStorage.getItem("blogs");
    if (savedBlogs) {
      setBlogs(JSON.parse(savedBlogs));
    } else {
      // Default blogs if none exist
      const defaultBlogs = [
        {
          id: 1,
          title: "Introduction to Technical Analysis",
          description: "Learn the basics of technical analysis in trading.",
          content: "Technical analysis is a method of evaluating securities by analyzing statistics generated by market activity. It uses historical price and volume data to predict future price movements. Technical analysts believe that all known information is already reflected in the price, and that price tends to move in trends. They use various tools like chart patterns, indicators, and oscillators to identify trading opportunities.",
          image: "https://via.placeholder.com/400x200/003366/FFFFFF?text=Technical+Analysis",
          category: "Trading",
          author: "SPi Academi",
          date: "2024-01-15"
        },
        {
          id: 2,
          title: "Understanding Moving Averages",
          description: "Why moving averages are so popular in trading.",
          content: "The Moving Average (MA) is one of the most commonly used technical indicators in trading. It is used to smooth out price data over a specific period, helping traders identify trends, support and resistance levels, and potential buy or sell signals. There are several types of moving averages including Simple Moving Average (SMA), Exponential Moving Average (EMA), and Weighted Moving Average (WMA). Each has its own characteristics and use cases.",
          image: "https://via.placeholder.com/400x200/5CE1E6/003366?text=Moving+Averages",
          category: "Indicators",
          author: "SPi Academi",
          date: "2024-01-20"
        },
        {
          id: 3,
          title: "Trading Psychology: Master Your Emotions",
          description: "Learn to control fear and greed for better trading decisions.",
          content: "Trading psychology is one of the most crucial aspects of successful trading. It involves understanding and managing the psychological factors that influence trading decisions. Fear and greed are the two primary emotions that can lead to poor trading choices. Fear can cause traders to exit positions too early, while greed can lead to holding positions too long or taking excessive risks. Successful traders develop discipline, patience, and emotional control to make rational decisions based on their trading plan rather than emotions.",
          image: "https://via.placeholder.com/400x200/FF6B6B/FFFFFF?text=Trading+Psychology",
          category: "Psychology",
          author: "SPi Academi",
          date: "2024-01-25"
        }
      ];
      setBlogs(defaultBlogs);
      localStorage.setItem("blogs", JSON.stringify(defaultBlogs));
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.description || !formData.content) {
      alert("Please fill in all required fields (Title, Description, and Content)");
      return;
    }

    if (editingBlog) {
      // Edit existing blog
      const updatedBlogs = blogs.map(blog => 
        blog.id === editingBlog.id ? { ...formData, id: blog.id } : blog
      );
      setBlogs(updatedBlogs);
      localStorage.setItem("blogs", JSON.stringify(updatedBlogs));
      alert("Blog updated successfully!");
      setEditingBlog(null);
    } else {
      // Add new blog
      const newBlog = {
        ...formData,
        id: Date.now(),
        date: new Date().toISOString().split('T')[0]
      };
      const updatedBlogs = [newBlog, ...blogs];
      setBlogs(updatedBlogs);
      localStorage.setItem("blogs", JSON.stringify(updatedBlogs));
      alert("Blog created successfully!");
    }
    resetForm();
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData(blog);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this blog?")) {
      const updatedBlogs = blogs.filter(blog => blog.id !== id);
      setBlogs(updatedBlogs);
      localStorage.setItem("blogs", JSON.stringify(updatedBlogs));
      alert("Blog deleted successfully!");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      content: "",
      image: "",
      category: "Trading",
      author: "SPi Academi",
      date: new Date().toISOString().split('T')[0]
    });
    setEditingBlog(null);
    setShowForm(false);
  };

  // Filter blogs based on search term
  const filteredBlogs = blogs.filter(blog =>
    blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog.author.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="blog-manager">
      <div className="blog-manager-header">
        <div className="blog-manager-title">
          <h3>📝 Blog Manager</h3>
          <span className="blog-count">{blogs.length} total blogs</span>
        </div>
        <button 
          className="add-blog-btn"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "✕ Cancel" : "➕ Add New Blog"}
        </button>
      </div>

      {/* Blog Form */}
      {showForm && (
        <div className="blog-form-container">
          <div className="blog-form-header">
            <h4>{editingBlog ? "✏️ Edit Blog" : "📝 Create New Blog"}</h4>
            {editingBlog && (
              <button className="cancel-edit-btn" onClick={resetForm}>
                Cancel Edit
              </button>
            )}
          </div>
          <form onSubmit={handleSubmit} className="blog-form">
            <div className="form-row">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter blog title"
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Trading">Trading</option>
                  <option value="Indicators">Indicators</option>
                  <option value="Psychology">Psychology</option>
                  <option value="Risk Management">Risk Management</option>
                  <option value="Market Analysis">Market Analysis</option>
                  <option value="Technical Analysis">Technical Analysis</option>
                  <option value="Fundamental Analysis">Fundamental Analysis</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Description *</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                placeholder="Short description (max 150 characters)"
                maxLength="150"
              />
              <small>{formData.description.length}/150 characters</small>
            </div>

            <div className="form-group">
              <label>Content *</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                required
                placeholder="Write your blog content here..."
                rows="8"
              />
            </div>

            <div className="form-group">
              <label>Featured Image URL</label>
              <input
                type="url"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                placeholder="https://example.com/image.jpg"
              />
              <small>Leave empty to use default placeholder image</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Author</label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="Author name"
                />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="save-btn">
                {editingBlog ? "✏️ Update Blog" : "💾 Save Blog"}
              </button>
              <button type="button" className="cancel-btn" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search Bar */}
      <div className="blog-search">
        <input
          type="text"
          placeholder="🔍 Search blogs by title, category, or author..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button className="clear-search" onClick={() => setSearchTerm("")}>
            ✕
          </button>
        )}
      </div>

      {/* Blog List */}
      <div className="blog-list">
        <div className="blog-list-header">
          <span>Title</span>
          <span>Category</span>
          <span>Date</span>
          <span>Actions</span>
        </div>
        {filteredBlogs.length === 0 ? (
          <div className="no-blogs">
            <p>{searchTerm ? "No blogs match your search." : "No blogs found. Click 'Add New Blog' to create one."}</p>
          </div>
        ) : (
          filteredBlogs.map((blog) => (
            <div key={blog.id} className="blog-item">
              <div className="blog-item-info">
                <div className="blog-item-title">{blog.title}</div>
                <div className="blog-item-meta">
                  <span className="blog-category">{blog.category}</span>
                  <span className="blog-date">{blog.date}</span>
                  <span className="blog-author">{blog.author}</span>
                </div>
              </div>
              <div className="blog-item-actions">
                <button 
                  className="edit-btn"
                  onClick={() => handleEdit(blog)}
                  title="Edit blog"
                >
                  ✏️
                </button>
                <button 
                  className="delete-btn"
                  onClick={() => handleDelete(blog.id)}
                  title="Delete blog"
                >
                  🗑️
                </button>
                <button 
                  className="view-btn"
                  onClick={() => window.open(`/article/${blog.id}`, '_blank')}
                  title="View blog"
                >
                  👁️
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="blog-stats">
        <span>Total Blogs: {blogs.length}</span>
        <span>|</span>
        <span>Showing: {filteredBlogs.length}</span>
        {searchTerm && <span>| Filtered by: "{searchTerm}"</span>}
      </div>
    </div>
  );
};

export default BlogManager;